import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import type { RootState } from '../store/store';
import AppBar from '../components/ora/Global/AppBar';
import NavBar from '../components/ora/Global/NavBar';
import DiscountBanner, {
  useDiscountBannerVisibility,
} from '../components/ora/Global/DiscountBanner';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { useCapability, useIsCourseStudent } from '../instances/useInstance';
import ExamSelectors, {
  desktopPillClassName,
} from '../components/ora/Global/ExamSelectors/ExamSelectors';
import { Dashboard } from '@/assets/svgs/Dashboard';
import { hapticsImpactLight } from '../utils/haptics';
import NotificationsBell from '../components/ora/Home/HomeHeader/NotificationsBell';
import ThemeToggleButton from '../components/ora/Home/HomeHeader/ThemeToggleButton';
import ShellBubble from '../components/ora/Global/CompanionShell/ShellBubble';
import OsDesktop from './OsDesktop';
import OsWaterBackground from './OsWaterBackground';
import OsWindow from './OsWindow';
import { useOsRouteMeta } from './useOsRouteMeta';
import { OsWindowVariantOverrideContext } from './OsWindowVariantOverride';
import {
  OsWindowMenuActionContext,
  useOsWindowMenuActionRegistry,
} from './OsWindowMenuAction';
import {
  OsWindowHeaderAddonContext,
  useOsWindowHeaderAddonRegistry,
} from './OsWindowHeaderAddon';
import {
  OS_DESKTOP_MEDIA_QUERY,
  isStudyRoute,
  type OsWindowVariant,
} from './osRouteMeta';
import { useAIDrawer } from '../hooks/useAIDrawer';
import {
  dashboardSearchFor,
  hasDesktopAskInSearch,
  landingDecision,
  MAP_QUERY_PARAM,
} from './landingDecision';
import {
  selectActiveConversationId,
  selectConversationMessageIds,
} from '@/slices/chat/chatSelectors';
import { useHostedChatDocument } from '../hooks/useChatDocumentViewer';
import { setOsFullscreenWindow } from './osFullscreenWindow';
import { useKnowledgeMapEnabled } from '../utils/knowledgeMapFlag';
import { useIsHistoryRailShown } from './historyRailStore';
import { useIsKnowledgeMapOpen } from './knowledgeMapOpenStore';
import { useIsMcGrawHill } from '../mcgrawHill/mcgrawHill'; // mcgraw-hill
import McGrawHillDesktopSelectors from '../mcgrawHill/McGrawHillDesktopSelectors'; // mcgraw-hill

// Course-only chrome (Courses V1), loaded only once a course student's
// desktop asks for it: everyone else's shell never fetches it.
const CourseProgressBar = lazy(
  () => import('../components/ora/Course/CourseProgressBar')
);

// The streak pill's chunk is fetched only once the bare desktop shows it.
const DesktopStreakBadge = lazy(() => import('./DesktopStreakBadge'));
const WINDOW_EXIT_MS = 300;

const OsShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  // A course student has no Dashboard: their desktop is the start screen,
  // whatever the dashboard-by-default setting says.
  const isCourseStudent = useIsCourseStudent();
  const isMcGrawHill = useIsMcGrawHill();
  // An instance that forces its theme has nothing for the toggle to change.
  const isThemeForced = useCapability('forcedTheme') !== 'none';

  const {
    isOpen: isAIDrawerOpen,
    open: openAIDrawer,
    hasMobileLauncher,
    setHasMobileNavRow,
    intercomUnreadCount,
  } = useAIDrawer();

  const isDesktopViewport = useMatchMedia(OS_DESKTOP_MEDIA_QUERY);
  // A side document open on the desktop itself (ORA-736).
  const desktopDocument = useHostedChatDocument('desktop');

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // `undefined` until a profile that knows the setting has been read (a
  // persisted profile from before the setting has no value for it).
  const defaultToDashboard = useSelector(
    (state: RootState) => state.user.profile?.defaultToDashboard
  );

  const isOnboarded = useSelector(
    (state: RootState) => state.user.profile?.isOnboarded
  );

  // The desktop's thread is the chat slice's active conversation (OsDesktop
  // hands the view no id of its own). A thread address in the query counts as
  // a conversation too: its messages land a moment after the URL does.
  const isDesktopChatEmpty = useSelector(
    (state: RootState) =>
      selectConversationMessageIds(state, selectActiveConversationId(state))
        .length === 0
  );

  const hasThreadAddress = new URLSearchParams(location.search).has(
    'conversationId'
  );

  // The "Open Dashboard by Default" setting: the desktop still lives at /,
  // but a session that OPENS there — a login, a cold load, the end of
  // onboarding — goes on to the Dashboard. Later visits to / (the AI nav
  // item, closing a window) are the student's own and stay. The / route's
  // loader makes the decision before anything paints when the persisted
  // profile can (see landingDecision); this is the fallback for a profile
  // that could not — it decides once, on the first route after onboarding,
  // as soon as the setting is known.
  const { pathname: currentPathname, search: currentSearch } = location;
  // `?map` keeps the desktop only for someone the map is offered to.
  const isKnowledgeMapEnabled = useKnowledgeMapEnabled();

  useEffect(() => {
    if (!landingDecision.isPending()) return;
    if (defaultToDashboard === undefined) return;
    // Onboarding is not the session's opening page: the decision waits for
    // the route it hands off to.
    if (currentPathname === '/onboarding') return;
    // Nor is a page a student reaches before onboarding (a game, a webinar,
    // help): the session opens where onboarding hands off (ORA-753).
    if (isOnboarded === false) return;
    landingDecision.settle();

    // A course student's desktop is their start screen: the setting is off
    // for them whatever the profile says (its switch is hidden too).
    if (!isDesktopViewport || !defaultToDashboard || isCourseStudent) return;
    if (currentPathname !== '/') return;
    if (hasDesktopAskInSearch(currentSearch, isKnowledgeMapEnabled)) return;

    navigate(`/dashboard${dashboardSearchFor(currentSearch)}`, {
      replace: true,
    });
  }, [
    currentPathname,
    currentSearch,
    defaultToDashboard,
    isCourseStudent,
    isDesktopViewport,
    isKnowledgeMapEnabled,
    isOnboarded,
    navigate,
  ]);

  // The window the session opens on paints in place: there is no desktop
  // on screen for it to arrive over, so the enter animation would only show
  // the (dimmed) desktop through it for 420ms (ORA-751). Later windows, and
  // the ones the fallback redirect above opens, still animate in.
  const initialLocationKeyRef = useRef(location.key);
  const isInitialWindow = location.key === initialLocationKeyRef.current;
  // True only while the close control's pre-navigation exit animation plays.
  // Route content always renders live — a stale route element must never be
  // kept mounted (its effects run against the new location and can navigate).
  const [isExiting, setIsExiting] = useState(false);
  // User-driven maximize for default windows; session-sticky across window
  // groups. Distinct from the route-declared `fullscreen` variant.
  const [isMaximized, setIsMaximized] = useState(false);

  // Pages can force a variant at runtime (e.g. game rounds go fullscreen);
  // cleared by the page's hook cleanup on unmount/round end.
  const [variantOverride, setVariantOverride] =
    useState<OsWindowVariant | null>(null);

  const { menuAction, registerMenuAction } = useOsWindowMenuActionRegistry();
  const { headerAddon, setHeaderAddon } = useOsWindowHeaderAddonRegistry();
  const currentMeta = useOsRouteMeta();

  // Routes without OS metadata (admin, redirect elements, anything not yet
  // migrated) render plainly outside the window system; the home route is the
  // one metadata-less route that intentionally shows the desktop itself.
  // A visitor reads the help docs on the marketing page (its own header and
  // footer, as on the website), not in a window over a desktop they do not
  // have.
  const isGuestHelpRoute =
    !isAuthenticated && location.pathname.startsWith('/help');

  const isPlainRoute =
    (!currentMeta && location.pathname !== '/') || isGuestHelpRoute;

  // The bare desktop: no window over it, at a desktop width.
  const isBareDesktop =
    isDesktopViewport && !currentMeta && location.pathname === '/';

  // The Dashboard is one press away from an empty chat: a pill along the
  // bottom edge, under the composer. Once a conversation is under way the
  // thread has the floor and the pill stands down (the rail's AI/Dashboard
  // items remain). The composer's bottom clearance follows it (body class:
  // the desktop's chat column is in another subtree).
  const showsDashboardPill =
    // mcgraw-hill: no Open Dashboard or streak pills on the desktop.
    !isMcGrawHill &&
    isBareDesktop &&
    isDesktopChatEmpty &&
    !hasThreadAddress &&
    !desktopDocument &&
    !isCourseStudent;

  // The knowledge map (ORA-867) takes the whole screen: the bare desktop's
  // floating chrome stands aside for it, still mounted (the bell's and the
  // exam selector's state survive), but invisible and out of reach.
  const isKnowledgeMapOpen = useIsKnowledgeMapOpen();
  // The streak pill sits in the lower-right corner, where the desktop's
  // chat-history rail runs down to (it spans the desktop's full height, so
  // there is no room above it): the pill stands down while the rail is on
  // screen, and while a side document takes the desktop, as the Dashboard
  // pill does. Under the open map it stays mounted and hidden like the rest
  // of the chrome, so its streak read is not repeated on every close. A
  // course student has no rail, so theirs stays.
  const isHistoryRailShown = useIsHistoryRailShown();

  const showsStreakPill =
    !isMcGrawHill && isBareDesktop && !isHistoryRailShown && !desktopDocument;

  const chromeHiddenProps = (
    isKnowledgeMapOpen ? { inert: '', 'aria-hidden': 'true' } : {}
  ) as Record<string, string>;

  useEffect(() => {
    document.body.classList.toggle(
      'os-desktop-pill-active',
      showsDashboardPill
    );

    return () => document.body.classList.remove('os-desktop-pill-active');
  }, [showsDashboardPill]);

  const group = currentMeta
    ? currentMeta.windowGroup || location.pathname
    : null;

  // Where the window has been since it opened: its header offers a back
  // control once there is somewhere inside it to go back to.

  const effectiveVariant = currentMeta
    ? (variantOverride ?? currentMeta.variant)
    : undefined;

  // Below the desktop breakpoint there is no desktop: the app opens on the
  // Dashboard and the companion is the sheet, reached from its floating
  // bubble. `/` forwards there, carrying any other query along (`activateExam`
  // from a shared-exam notification); a thread address — a study set's cold
  // return to `/?conversationId=…&blockId=…` — reopens that thread in the
  // sheet instead, since the page that would show it does not exist here, and
  // so does `openChat`, which a legacy bare /chat link redirects with.
  useEffect(() => {
    if (isDesktopViewport || location.pathname !== '/') return;

    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversationId');
    const blockId = params.get('blockId');
    const shouldOpenChat = params.has('openChat');
    params.delete('conversationId');
    params.delete('blockId');
    params.delete('openChat');
    // The map is desktop-only; the Dashboard has no use for the ask.
    params.delete(MAP_QUERY_PARAM);
    const search = params.toString();

    navigate(`/dashboard${search ? `?${search}` : ''}`, { replace: true });

    if (conversationId) {
      openAIDrawer({ conversationId, scrollToBlockId: blockId ?? undefined });
    } else if (shouldOpenChat) {
      openAIDrawer();
    }
  }, [
    isDesktopViewport,
    location.pathname,
    location.search,
    navigate,
    openAIDrawer,
  ]);

  const exitTimeoutRef = useRef<number | null>(null);

  const closeWindow = () => {
    if (isExiting || !group) return;

    setIsExiting(true);

    exitTimeoutRef.current = window.setTimeout(() => {
      exitTimeoutRef.current = null;
      navigate('/');
    }, WINDOW_EXIT_MS);
  };

  // isExiting resets only AFTER the route actually changes. Clearing it
  // alongside navigate() re-rendered the window un-exited (full opacity) for
  // the duration of the router-transition commit — a visible flicker between
  // the fade-out and the desktop fade-in. The cleanup also cancels a pending
  // close if the user navigates elsewhere mid-animation, so it can't still
  // yank them to the desktop when it fires.
  useEffect(() => {
    setIsExiting(false);

    return () => {
      if (exitTimeoutRef.current !== null) {
        window.clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
    };
  }, [location.pathname]);

  // Esc closes the active default window back to the desktop — but only when
  // nothing else owns the key: no open dialog/menu/popover, no focused form
  // field, and not while the AI companion is open (its own Esc closes it
  // first).
  // A maximized window restores on the first Esc, closes on the second.
  // Fullscreen-variant routes (study, games) are exempt, matching their lack
  // of a close control.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isEscapeKey = event.key === 'Escape';

      const isMaximizeKey =
        (event.key === 'm' || event.key === 'M') &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey;

      if ((!isEscapeKey && !isMaximizeKey) || event.defaultPrevented) return;
      if (effectiveVariant !== 'default') return;
      // Sub-md the window is the whole screen: nothing to maximize, and no
      // desktop underneath to close back to.
      if (!isDesktopViewport) return;

      // Games take bare letters as input (Doctordle guesses) from their own
      // window listener, whose order relative to this one is not stable.
      if (isMaximizeKey && location.pathname.startsWith('/games')) return;

      const target = event.target as HTMLElement | null;

      if (
        target?.isContentEditable ||
        target?.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
        )
      )
        return;

      if (
        document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="menu"], [data-radix-popper-content-wrapper]'
        )
      )
        return;

      if (isMaximizeKey) {
        setIsMaximized((value) => !value);
        return;
      }

      // Escape: the AI companion's own Esc handler closes it first.
      if (isAIDrawerOpen) return;

      if (isMaximized) {
        setIsMaximized(false);
        return;
      }

      closeWindow();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // The shell owns the nav on the desktop and over default windows (pages
  // hide their embedded navbar inside a window — see _os-shell.scss). Fullscreen
  // routes never rendered a navbar, and plain routes bring their own.
  const showShellNav =
    !isPlainRoute && (!currentMeta || effectiveVariant === 'default');

  // The discount banner runs along the very top of the app, above the nav and
  // windows. Fullscreen routes (study, games in round) stay immersive, and
  // plain routes (pricing, admin-adjacent tooling outside the shell) render
  // their own.
  const isDiscountBannerVisible = useDiscountBannerVisibility();

  const showShellBanner =
    isDiscountBannerVisible &&
    !isPlainRoute &&
    (!currentMeta || effectiveVariant === 'default');

  // Body-level class: the nav, windows, and AI surfaces live in different
  // subtrees (AIDrawerRenderer mounts outside the shell), so the geometry
  // offsets in _os-shell.scss key off the body.
  useEffect(() => {
    document.body.classList.toggle('os-banner-active', showShellBanner);
    return () => document.body.classList.remove('os-banner-active');
  }, [showShellBanner]);

  // The companion reads this to decide where its launcher goes: in this row
  // when there is one, floating over the page when there is not. The row is
  // a sub-md surface (the CSS hides it from 900px), so it must not be
  // reported at desktop widths — with the floating shell off, the companion
  // would stand its bubble down for a row nobody can see.
  const hasMobileNavRow = showShellNav && !isDesktopViewport;

  useEffect(() => {
    setHasMobileNavRow(hasMobileNavRow);
    return () => setHasMobileNavRow(false);
  }, [hasMobileNavRow, setHasMobileNavRow]);

  // Window routes (dashboard, games, study, ...) hide Intercom's floating
  // launcher — support is reachable via the account menu instead. The desktop
  // and plain routes keep it. CSS-based (see _os-shell.scss) so it applies
  // regardless of when the lazily-loaded widget boots.
  const isWindowRoute = Boolean(currentMeta) && !isPlainRoute;

  useEffect(() => {
    document.body.classList.toggle('os-intercom-hidden', isWindowRoute);
    return () => document.body.classList.remove('os-intercom-hidden');
  }, [isWindowRoute]);

  // Chrome outside the router (the Audio Overview bar) shrinks out of the way
  // of fullscreen windows, including a game round's runtime override.
  const isFullscreenWindow =
    Boolean(currentMeta) && !isPlainRoute && effectiveVariant === 'fullscreen';

  useEffect(() => {
    setOsFullscreenWindow(isFullscreenWindow);
    return () => setOsFullscreenWindow(false);
  }, [isFullscreenWindow]);

  // While a default window is maximized, the nav hides behind a left-edge
  // hover/focus reveal instead of sitting beside the window.
  const isNavRevealMode =
    isDesktopViewport && isMaximized && effectiveVariant === 'default';

  return (
    <OsWindowMenuActionContext.Provider value={registerMenuAction}>
      <OsWindowHeaderAddonContext.Provider value={setHeaderAddon}>
        <OsWindowVariantOverrideContext.Provider value={setVariantOverride}>
          {showShellBanner && (
            <div className="os-banner">
              <DiscountBanner />
            </div>
          )}
          {/* On the bare desktop the exam selector sits top-center and the
              notifications bell + theme toggle top-right; with a window open
              the selector moves into the Dashboard window's header. */}
          {isBareDesktop && (
            <>
              <div
                className={cn(
                  'os-desktop-selectors',
                  isKnowledgeMapOpen && 'os-desktop-chrome--hidden'
                )}
                {...chromeHiddenProps}
              >
                {/* mcgraw-hill: the Clinical / Study toggle (ORA-1265). */}
                {isMcGrawHill ? (
                  <McGrawHillDesktopSelectors />
                ) : (
                  <ExamSelectors variant="header" />
                )}
              </div>
              <div
                className={cn(
                  'os-desktop-controls',
                  isKnowledgeMapOpen && 'os-desktop-chrome--hidden'
                )}
                {...chromeHiddenProps}
              >
                <NotificationsBell />
                {!isThemeForced && <ThemeToggleButton />}
              </div>
              {/* The study streak, in the lower-right corner beside the
                companion launcher, while the desktop is the surface and
                neither the history rail nor a side document is there. */}
              {showsStreakPill && (
                <div
                  className={cn(
                    isKnowledgeMapOpen && 'os-desktop-chrome--hidden'
                  )}
                  {...chromeHiddenProps}
                >
                  <Suspense fallback={null}>
                    <DesktopStreakBadge />
                  </Suspense>
                </div>
              )}
              {/* A course student's desktop carries the course's progress
                along the bottom edge instead: a segment per module and the
                overall figure. */}
              {isBareDesktop && isCourseStudent && (
                <div
                  className={cn(
                    'os-desktop-dashboard',
                    isKnowledgeMapOpen && 'os-desktop-chrome--hidden'
                  )}
                  {...chromeHiddenProps}
                >
                  <Suspense fallback={null}>
                    <CourseProgressBar />
                  </Suspense>
                </div>
              )}
              {/* The Dashboard is one press away from an empty chat: a pill
                along the bottom edge, under the composer. Once a conversation
                is under way, or a side document takes the desktop down to that
                edge, the pill stands down (the rail's AI/Dashboard items
                remain). */}
              {showsDashboardPill && (
                <div
                  className={cn(
                    'os-desktop-dashboard',
                    isKnowledgeMapOpen && 'os-desktop-chrome--hidden'
                  )}
                  {...chromeHiddenProps}
                >
                  <button
                    className={cn(
                      desktopPillClassName,
                      'py-2.5 [&_svg]:size-4'
                    )}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/dashboard');
                    }}
                    type="button"
                  >
                    <Dashboard />
                    Open Dashboard
                  </button>
                </div>
              )}
            </>
          )}
          <div className={`os-root ${isPlainRoute ? 'os-root--plain' : ''}`}>
            {/* The water is the OS behind every window — faculty's included;
              only study sessions, which cover the viewport, go without. */}
            {!isPlainRoute && !isStudyRoute(location.pathname) && (
              <OsWaterBackground isActive={location.pathname === '/'} />
            )}
            <div className="os-root__perspective">
              {/* Study sessions cover the viewport, faculty accounts have no
                  student chat surface, and onboarding gates the app before
                  any of it applies — mounting the desktop (and its chat tree)
                  under these is pure overhead. Sub-md there is no desktop at
                  all (the window is full-bleed and / goes to the Dashboard). */}
              {isDesktopViewport &&
                !isStudyRoute(location.pathname) &&
                !location.pathname.startsWith('/faculty') &&
                location.pathname !== '/onboarding' && (
                  <OsDesktop
                    isActive={!currentMeta && location.pathname === '/'}
                    isDimmed={Boolean(currentMeta)}
                  />
                )}
              <div className="os-root__stage">
                {/* ONE persistent window across every windowed route — content
                    swaps inside it (200ms crossfade), so the big enter
                    animation only plays when a window first opens over the
                    desktop, not on every navigation. */}
                {!isPlainRoute && currentMeta && group && (
                  <OsWindow
                    menuAction={menuAction}
                    scrollResetKey={group}
                    isExiting={isExiting}
                    isInstant={isInitialWindow}
                    isMaximized={isMaximized}
                    title={currentMeta.title}
                    headerAddon={headerAddon}
                    showsExamSelectors={Boolean(currentMeta.hasExamSelectors)}
                    hidesMobileHeader={Boolean(currentMeta.hidesMobileHeader)}
                    onClose={closeWindow}
                    onToggleMaximize={() => setIsMaximized((value) => !value)}
                    variant={effectiveVariant ?? currentMeta.variant}
                  >
                    {outlet}
                  </OsWindow>
                )}
              </div>
            </div>
          </div>
          {showShellNav && (
            <>
              {isNavRevealMode ? (
                <div className="os-nav-reveal">
                  <NavBar mode={currentMeta?.navMode ?? 'study'} />
                </div>
              ) : (
                <div className="os-desktop-nav">
                  <NavBar mode={currentMeta?.navMode ?? 'study'} />
                </div>
              )}
              {/* One row along the bottom edge: the tabs, and the companion's
                  launcher beside them. The drawer floats its own copy only
                  where there is no row to put it in (see AIDrawerRenderer). */}
              <div className="os-mobile-nav">
                <AppBar />
                {hasMobileLauncher && (
                  <ShellBubble
                    className="os-mobile-nav__launcher"
                    isHidden={isAIDrawerOpen}
                    onClick={openAIDrawer}
                    unreadCount={intercomUnreadCount}
                  />
                )}
              </div>
            </>
          )}
          {isPlainRoute && outlet}
        </OsWindowVariantOverrideContext.Provider>
      </OsWindowHeaderAddonContext.Provider>
    </OsWindowMenuActionContext.Provider>
  );
};

export default OsShell;
